/**
 * "My Today" urgent items for the floating panel: time-critical things a
 * customer asked for on WhatsApp, plus today's site visits.
 *
 *  - Call-back requested (lead.websiteRequest type callback-request, pending,
 *    due today or overdue)
 *  - Site visit today (lead.appointment scheduled for today)
 *  - Appointment WhatsApp delivery failure (manual call needed)
 *  - Needs the team (whatsappContactState.needsHuman: talk to team, running
 *    late, payment / document questions...)
 *
 * Scope: "mine" = leads assigned to me; managers and admins also see
 * unassigned ones, and with Team on, everyone's.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';

export type UrgentKind = 'callback' | 'visit' | 'visitRequest' | 'visitTask' | 'visitMessageFailure' | 'human' | 'reengaged';

export interface UrgentItem {
  id: string;
  kind: UrgentKind;
  leadId: string | null;
  name: string;
  phone10: string;
  /** When it is due (call-back time, visit time, or when they asked). */
  dueAt: Date | null;
  label: string;
  ownerEmail: string | null;
  ownerName: string | null;
  /** Human-request items only: true after every supported lead-phone lookup finishes. */
  leadLookupComplete?: boolean;
  bookedOnWhatsapp?: boolean;
  /** Closed lead re-engaged: why it was closed, e.g. "Not interested". */
  closeInfo?: string | null;
  /** Came back after closing, but the message looks like a sales pitch (hint only). */
  possiblePitch?: boolean;
  /** today = due today or overdue; tomorrow = shown in the collapsed group. */
  bucket: 'today' | 'tomorrow';
  /** Visits only: appointment status (scheduled, completed, no-show, cancelled). */
  visitStatus?: string;
  /** Visits only: what the customer said on WhatsApp. */
  customerNote?: string | null;
  /** Visits only: earlier day (still unmarked), today, or a later day. */
  visitDay?: 'past' | 'today' | 'future';
  /** Visits only: checked in at the front desk today ("At reception", "With Pratik"). */
  receptionLabel?: string | null;
  /** Visits only: when the customer tapped "Running late" on WhatsApp. */
  runningLateAt?: Date | null;
}

export type VisitPhase = 'upcoming' | 'soon' | 'now' | 'unmarked' | 'done' | 'no-show' | 'cancelled';

/** Visits stay "now" for an hour after the time; after that they need an outcome. */
const UNMARKED_AFTER_MS = 60 * 60000;
/** A customer who said "Running late" gets 3 hours before the visit counts as not marked. */
const UNMARKED_AFTER_LATE_MS = 3 * 60 * 60000;

export const visitPhase = (item: UrgentItem, now = new Date()): VisitPhase => {
  const s = String(item.visitStatus || 'scheduled');
  if (s === 'completed') return 'done';
  if (s === 'no-show' || s === 'cancelled') return s;
  if (!item.dueAt) return 'upcoming';
  const diff = now.getTime() - item.dueAt.getTime();
  if (diff > (item.runningLateAt ? UNMARKED_AFTER_LATE_MS : UNMARKED_AFTER_MS)) return 'unmarked';
  if (diff >= -15 * 60000) return 'now';
  if (diff >= -60 * 60000) return 'soon';
  return 'upcoming';
};

/** Scheduled call-backs enter the action queue shortly before their due time. */
export const needsActionNow = (item: UrgentItem, now = new Date()) => {
  if (item.bucket !== 'today') return false;
  if (item.kind === 'visit') return visitPhase(item, now) === 'unmarked';
  if (item.kind === 'callback' && item.dueAt) {
    return item.dueAt.getTime() - now.getTime() <= 30 * 60000;
  }
  return true;
};

type LeadRow = { id: string; data: Record<string, any> };

const endOfToday = () => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
};

const endOfTomorrow = () => new Date(endOfToday().getTime() + 86400000);

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const phone10Of = (data: Record<string, any>) => {
  const raw = String(data.phone10 || data.phone || '').replace(/\D/g, '');
  return raw.length >= 10 ? raw.slice(-10) : '';
};

const toDate = (v: any): Date | null => (v?.toDate ? v.toDate() : null);

const timeLabel = (d: Date) => d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });

// Today's front desk state of a visit, from lead.receptionVisit.
const receptionLabelOf = (rv: any): string | null => {
  if (!rv || rv.dayKey !== new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10)) return null;
  if (rv.status === 'waiting') return 'At reception';
  if (rv.status === 'with') return `With ${rv.presenterName || rv.presenterEmail || 'team'}`;
  if (rv.status === 'left') return 'Left reception';
  return null;
};

export const useMyTodayUrgent = (db: Firestore, myEmail: string, canSeeTeam: boolean, team: boolean, enabled = true) => {
  const [callbackLeads, setCallbackLeads] = useState<LeadRow[]>([]);
  const [visitRequestLeads, setVisitRequestLeads] = useState<LeadRow[]>([]);
  const [reengagedLeads, setReengagedLeads] = useState<LeadRow[]>([]);
  const [visitLeads, setVisitLeads] = useState<LeadRow[]>([]);
  const [visitTaskLeads, setVisitTaskLeads] = useState<LeadRow[]>([]);
  const [visitMessageFailureLeads, setVisitMessageFailureLeads] = useState<LeadRow[]>([]);
  const [humanStates, setHumanStates] = useState<Array<{ phone10: string; data: Record<string, any> }>>([]);
  const [humanLeads, setHumanLeads] = useState<Map<string, LeadRow>>(new Map());
  const [whatsappContactNames, setWhatsappContactNames] = useState<Map<string, string>>(new Map());
  const [whatsappProfileNames, setWhatsappProfileNames] = useState<Map<string, string>>(new Map());
  const [resolvedHumanPhones, setResolvedHumanPhones] = useState<Set<string>>(new Set());

  // Pending WhatsApp call-back requests.
  useEffect(
    () => {
      if (!enabled) return;
      return onSnapshot(
        // Every WhatsApp-originated request (new and older formats).
        query(collection(db, 'leads'), where('websiteRequest.source', '==', 'whatsapp-auto-reply'), limit(500)),
        (snap) => setCallbackLeads(snap.docs.map((d) => ({ id: d.id, data: d.data() }))),
        () => setCallbackLeads([])
      );
    },
    [db, enabled]
  );

  // Use the same saved WhatsApp names shown in Messages and the latest inbound
  // profile name when an unlinked contact has not been saved in CRM yet.
  useEffect(() => {
    if (!enabled) return;
    return onSnapshot(
      query(collection(db, 'whatsappContacts'), limit(1000)),
      (snap) => {
        const names = new Map<string, string>();
        snap.docs.forEach((row) => {
          const data = row.data();
          const phone10 = String(data.phone || row.id).replace(/\D/g, '').slice(-10);
          const name = String(data.confirmedName || '').trim();
          if (phone10 && name) names.set(phone10, name);
        });
        setWhatsappContactNames(names);
      },
      () => setWhatsappContactNames(new Map())
    );
  }, [db, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const phones = Array.from(new Set(humanStates.map(({ phone10 }) => String(phone10 || '').replace(/\D/g, '').slice(-10)).filter((phone10) => phone10.length === 10)));
    if (!phones.length) { setWhatsappProfileNames(new Map()); return; }
    let cancelled = false;
    const chunks: string[][] = [];
    for (let index = 0; index < phones.length; index += 30) chunks.push(phones.slice(index, index + 30).map((phone10) => `91${phone10}`));
    void Promise.all(chunks.map((chunk) => getDocs(query(
      collection(db, 'whatsappMessages'),
      where('contactPhone', 'in', chunk),
      limit(500),
    )))).then((snapshots) => {
      if (cancelled) return;
      const latest = new Map<string, { name: string; at: number }>();
      snapshots.forEach((snapshot) => snapshot.docs.forEach((row) => {
        const data = row.data();
        const phone10 = String(data.contactPhone || '').replace(/\D/g, '').slice(-10);
        const name = String(data.fromName || '').trim();
        if (!phone10 || !name) return;
        const at = typeof data.createdAt?.toMillis === 'function' ? data.createdAt.toMillis() : 0;
        const current = latest.get(phone10);
        if (!current || at > current.at) latest.set(phone10, { name, at });
      }));
      setWhatsappProfileNames(new Map(Array.from(latest, ([phone10, row]) => [phone10, row.name])));
    }).catch(() => { if (!cancelled) setWhatsappProfileNames(new Map()); });
    return () => { cancelled = true; };
  }, [db, enabled, humanStates]);

  // Closed leads back on WhatsApp, waiting for Reopen / Ignore.
  useEffect(
    () => {
      if (!enabled) return;
      return onSnapshot(
        query(collection(db, 'leads'), where('whatsappReengagement.status', '==', 'pending'), limit(200)),
        (snap) => setReengagedLeads(snap.docs.map((d) => ({ id: d.id, data: d.data() }))),
        () => setReengagedLeads([])
      );
    },
    [db, enabled]
  );

  // Older visit picks that never became an appointment (from the clean-up).
  useEffect(
    () => {
      if (!enabled) return;
      return onSnapshot(
        query(collection(db, 'leads'), where('whatsappVisitRequest.status', '==', 'pending'), limit(200)),
        (snap) => setVisitRequestLeads(snap.docs.map((d) => ({ id: d.id, data: d.data() }))),
        () => setVisitRequestLeads([])
      );
    },
    [db, enabled]
  );

  // Call tasks: re-book after "Didn't come" / a WhatsApp cancel, or no reply in 2 days.
  useEffect(
    () => {
      if (!enabled) return;
      return onSnapshot(
        query(collection(db, 'leads'), where('visitFollowUpTask.status', '==', 'pending'), limit(200)),
        (snap) => setVisitTaskLeads(snap.docs.map((d) => ({ id: d.id, data: d.data() }))),
        () => setVisitTaskLeads([])
      );
    },
    [db, enabled]
  );

  // A confirmation/reminder that Meta marks undeliverable needs a manual call.
  useEffect(
    () => {
      if (!enabled) return;
      return onSnapshot(
        query(collection(db, 'leads'), where('whatsappVisitDeliveryTask.status', '==', 'pending'), limit(200)),
        (snap) => setVisitMessageFailureLeads(snap.docs.map((d) => ({ id: d.id, data: d.data() }))),
        () => setVisitMessageFailureLeads([])
      );
    },
    [db, enabled]
  );

  // Site visits from 3 days ago (unmarked ones) through tomorrow.
  useEffect(
    () => {
      if (!enabled) return;
      return onSnapshot(
        query(
          collection(db, 'leads'),
          where('appointment.scheduledAt', '>=', Timestamp.fromDate(new Date(startOfToday().getTime() - 3 * 86400000))),
          where('appointment.scheduledAt', '<=', Timestamp.fromDate(endOfTomorrow())),
          limit(300)
        ),
        (snap) => setVisitLeads(snap.docs.map((d) => ({ id: d.id, data: d.data() }))),
        () => setVisitLeads([])
      );
    },
    [db, enabled]
  );

  // Customers waiting for a person (talk to team, running late, questions).
  useEffect(
    () => {
      if (!enabled) return;
      return onSnapshot(
        query(collection(db, 'whatsappContactState'), where('needsHuman', '==', true), limit(200)),
        (snap) => setHumanStates(snap.docs.map((d) => ({ phone10: d.id, data: d.data() }))),
        () => setHumanStates([])
      );
    },
    [db, enabled]
  );

  // Lead for each waiting customer (by the lead id we stored, else by phone).
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setResolvedHumanPhones(new Set());
    (async () => {
      const next = new Map<string, LeadRow>();
      for (const s of humanStates) {
        const phone10 = String(s.phone10 || '').replace(/\D/g, '').slice(-10);
        if (!phone10) continue;
        const leadId = String(s.data.openRequest?.leadId || s.data.pendingBusyNoteLeadId || '');
        if (leadId) {
          const snap = await getDoc(doc(db, 'leads', leadId)).catch(() => null);
          if (snap?.exists()) {
            next.set(phone10, { id: snap.id, data: snap.data() });
            continue;
          }
        }

        // New leads have phone10. Older imports may only have phoneSearch or
        // the full phone value, so try every representation used by the CRM.
        const candidates = [
          query(collection(db, 'leads'), where('phone10', '==', phone10), limit(1)),
          query(collection(db, 'leads'), where('phoneSearch', 'array-contains', phone10), limit(1)),
          query(collection(db, 'leads'), where('phone', '==', `+91${phone10}`), limit(1)),
          query(collection(db, 'leads'), where('phone', '==', `91${phone10}`), limit(1)),
          query(collection(db, 'leads'), where('phone', '==', phone10), limit(1)),
        ];
        for (const candidate of candidates) {
          const byPhone = await getDocs(candidate).catch(() => null);
          if (byPhone && !byPhone.empty) {
            next.set(phone10, { id: byPhone.docs[0].id, data: byPhone.docs[0].data() });
            break;
          }
        }
      }
      if (!cancelled) {
        setHumanLeads(next);
        setResolvedHumanPhones(new Set(humanStates.map((state) => String(state.phone10 || '').replace(/\D/g, '').slice(-10)).filter(Boolean)));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [db, humanStates, enabled]);

  const items = useMemo(() => {
    const me = myEmail.trim().toLowerCase();
    const inScope = (owner: string | null) => {
      const o = String(owner || '').trim().toLowerCase();
      if (o && o === me) return true;
      if (!canSeeTeam) return false;
      if (team) return true;
      return !o; // managers and admins: unassigned by default
    };
    const end = endOfToday().getTime();
    const endTomorrow = endOfTomorrow().getTime();
    const bucketOf = (due: Date | null): 'today' | 'tomorrow' | null =>
      !due || due.getTime() <= end ? 'today' : due.getTime() <= endTomorrow ? 'tomorrow' : null;
    const out: UrgentItem[] = [];

    callbackLeads.forEach(({ id, data }) => {
      const wr = data.websiteRequest || {};
      if (String(wr.status || '') !== 'pending') return;
      const isCallback = wr.type === 'callback-request' || /call\s*back/i.test(String(wr.label || ''));
      if (!isCallback) return;
      const due = toDate(wr.dueAt) || toDate(data.nextFollowUpAt) || toDate(data.nextFollowUpDate);
      const bucket = bucketOf(due);
      if (!bucket) return;
      if (!inScope(data.assignedTo)) return;
      out.push({
        id: `cb-${id}`,
        kind: 'callback',
        leadId: id,
        name: String(data.name || 'Customer'),
        phone10: phone10Of(data),
        dueAt: due,
        label: String(wr.label || 'Call back').replace(/^WhatsApp:\s*/i, ''),
        ownerEmail: data.assignedTo || null,
        ownerName: data.assignedToName || null,
        bucket,
      });
    });

    reengagedLeads.forEach(({ id, data }) => {
      const r = data.whatsappReengagement || {};
      if (!inScope(data.assignedTo)) return;
      const closedAt = toDate(r.closedAt);
      out.push({
        id: `re-${id}`,
        kind: 'reengaged',
        leadId: id,
        name: String(data.name || 'Customer'),
        phone10: phone10Of(data),
        dueAt: toDate(r.firstAt),
        label: String(r.detail || 'Came back on WhatsApp'),
        closeInfo: `Closed${closedAt ? ` ${closedAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` : ''}${r.closeReason ? ` (${r.closeReason})` : ''}`,
        possiblePitch: r.possiblePitch === true,
        ownerEmail: data.assignedTo || null,
        ownerName: data.assignedToName || null,
        bucket: 'today',
      });
    });

    visitRequestLeads.forEach(({ id, data }) => {
      const req = data.whatsappVisitRequest || {};
      // Done once any appointment was set after the request.
      const apptSetAt = toDate(data.appointment?.createdAt) || toDate(data.appointment?.updatedAt);
      const requestedAt = toDate(req.requestedAt);
      if (apptSetAt && (!requestedAt || apptSetAt.getTime() >= requestedAt.getTime())) return;
      if (!inScope(data.assignedTo)) return;
      out.push({
        id: `vr-${id}`,
        kind: 'visitRequest',
        leadId: id,
        name: String(data.name || 'Customer'),
        phone10: phone10Of(data),
        dueAt: toDate(req.requestedAt),
        label: `Visit requested${req.label ? ` (${req.label})` : ''}: needs scheduling`,
        ownerEmail: data.assignedTo || null,
        ownerName: data.assignedToName || null,
        bucket: 'today',
      });
    });

    visitLeads.forEach(({ id, data }) => {
      const appt = data.appointment || {};
      const status = String(appt.status || 'scheduled');
      if (appt.type && appt.type !== 'site-visit') return;
      if (!inScope(data.assignedTo)) return;
      const at = toDate(appt.scheduledAt);
      const bucket = bucketOf(at) || 'tomorrow';
      const visitDay = at && at.getTime() < startOfToday().getTime() ? 'past' : bucket === 'tomorrow' ? 'future' : 'today';
      // Earlier days and tomorrow: only visits still on (unmarked / coming);
      // today: every outcome (Visits tab).
      if (visitDay !== 'today' && status !== 'scheduled') return;
      const confirmedAt = toDate(appt.customerConfirmedAt);
      const lateAt = toDate(appt.customerRunningLateAt);
      const movedAt = toDate(appt.movedOnWhatsappAt);
      const customerNote = lateAt
        ? `⏱ Running late (${timeLabel(lateAt)})`
        : confirmedAt
          ? '✅ "I\'ll be there"'
          : movedAt
            ? '🔁 Moved on WhatsApp'
            : null;
      out.push({
        id: `visit-${id}`,
        kind: 'visit',
        leadId: id,
        name: String(data.name || 'Customer'),
        phone10: phone10Of(data),
        dueAt: at,
        label: `Site visit${at ? ` · ${timeLabel(at)}` : ''}${appt.projectName ? ` · ${appt.projectName}` : ''}`,
        ownerEmail: data.assignedTo || null,
        ownerName: data.assignedToName || null,
        bookedOnWhatsapp: appt.bookedVia === 'whatsapp',
        bucket,
        visitStatus: status,
        customerNote,
        visitDay,
        receptionLabel: receptionLabelOf(data.receptionVisit),
        runningLateAt: lateAt,
      });
    });

    visitTaskLeads.forEach(({ id, data }) => {
      const task = data.visitFollowUpTask || {};
      const due = toDate(task.dueAt);
      const bucket = bucketOf(due);
      if (!bucket) return;
      if (!inScope(data.assignedTo)) return;
      out.push({
        id: `vt-${id}`,
        kind: 'visitTask',
        leadId: id,
        name: String(data.name || 'Customer'),
        phone10: phone10Of(data),
        dueAt: due,
        label: String(task.label || 'Call to re-book the site visit'),
        ownerEmail: data.assignedTo || null,
        ownerName: data.assignedToName || null,
        bucket,
      });
    });

    visitMessageFailureLeads.forEach(({ id, data }) => {
      const task = data.whatsappVisitDeliveryTask || {};
      const due = toDate(task.dueAt) || toDate(task.failedAt);
      const bucket = bucketOf(due);
      if (!bucket || !inScope(data.assignedTo)) return;
      out.push({
        id: `visit-message-failure-${id}`,
        kind: 'visitMessageFailure',
        leadId: id,
        name: String(data.name || 'Customer'),
        phone10: phone10Of(data),
        dueAt: due,
        label: String(task.label || 'Visit WhatsApp message failed · call customer'),
        ownerEmail: data.assignedTo || null,
        ownerName: data.assignedToName || null,
        bucket,
      });
    });

    humanStates.forEach(({ phone10: storedPhone, data }) => {
      const phone10 = String(storedPhone || data.phone10 || data.contactPhone || '').replace(/\D/g, '').slice(-10);
      const lead = humanLeads.get(phone10);
      const owner = lead?.data.assignedTo || null;
      if (!inScope(owner)) return;
      out.push({
        id: `human-${phone10}`,
        kind: 'human',
        leadId: lead?.id || null,
        name: String(lead?.data.name || whatsappContactNames.get(phone10) || whatsappProfileNames.get(phone10) || data.contactName || `+91 ${phone10}`),
        phone10,
        dueAt: toDate(data.needsHumanAt),
        label: String(data.needsHumanReason || 'Wants to talk to the team'),
        ownerEmail: owner,
        ownerName: lead?.data.assignedToName || null,
        leadLookupComplete: resolvedHumanPhones.has(phone10),
        bucket: 'today',
      });
    });

    // Overdue / earliest first; items without a time last.
    return out.sort((a, b) => (a.dueAt?.getTime() ?? Infinity) - (b.dueAt?.getTime() ?? Infinity));
  }, [callbackLeads, reengagedLeads, visitRequestLeads, visitLeads, visitTaskLeads, visitMessageFailureLeads, humanStates, humanLeads, whatsappContactNames, whatsappProfileNames, resolvedHumanPhones, myEmail, canSeeTeam, team]);

  return enabled ? items : [];
};

/** "Overdue 2h", "Due 4:00 pm", "Since 11:20 am" style status for a row. */
export const dueText = (item: UrgentItem, now = new Date()) => {
  if (!item.dueAt) return '';
  const diffMin = Math.round((now.getTime() - item.dueAt.getTime()) / 60000);
  if (item.kind === 'human' || item.kind === 'reengaged') return `Waiting since ${timeLabel(item.dueAt)}`;
  if (item.kind === 'visitRequest') return `Asked ${item.dueAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`;
  if (item.bucket === 'tomorrow') return `Tomorrow ${timeLabel(item.dueAt)}`;
  if (item.kind === 'visit') {
    const phase = visitPhase(item, now);
    if (phase === 'done') return 'Done ✓';
    if (phase === 'no-show') return 'No-show';
    if (phase === 'cancelled') return 'Cancelled';
    if (item.receptionLabel) return item.receptionLabel;
    if (phase === 'unmarked' && item.visitDay === 'past') {
      return `Not marked · ${item.dueAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`;
    }
    if (phase === 'unmarked') return `Not marked · ${Math.max(1, Math.round(diffMin / 60))}h past`;
    if (item.runningLateAt && diffMin >= -60) return `Running late · told ${timeLabel(item.runningLateAt)}`;
    if (phase === 'now') return 'Now';
    if (phase === 'soon') return `In ${-diffMin} min`;
    return timeLabel(item.dueAt);
  }
  if (diffMin <= 0) return `Due ${timeLabel(item.dueAt)}`;
  if (diffMin < 60) return `Overdue ${diffMin} min`;
  const h = Math.round(diffMin / 60);
  return h >= 24 ? `Overdue ${Math.round(h / 24)}d` : `Overdue ${h}h`;
};

export const isOverdue = (item: UrgentItem, now = new Date()) => {
  if (!item.dueAt) return false;
  // Re-engaged closed leads turn red after 2 hours without action.
  if (item.kind === 'reengaged') return now.getTime() - item.dueAt.getTime() > 2 * 3600000;
  if (item.kind === 'visit') return visitPhase(item, now) === 'unmarked';
  return item.kind !== 'human' && item.kind !== 'visitRequest' && item.dueAt.getTime() < now.getTime();
};
